import {
  CategoryDto,
  CheckInDto,
  MemberDto,
  OrganisationActivityDto,
} from '@careapp-group/ng-sdk-v2-new';
import { createSelector } from '@ngrx/store';
import { addDays, differenceInMilliseconds, isBefore, millisecondsToMinutes } from 'date-fns';
import { DatePipe } from '../../shared/pipes';
import { checkInsForSelectedCustomer } from '../check-ins/check-ins.selectors';
import * as reducers from '../reducers';
import { CHECK_INS, CUSTOMERS, getAll, getEntities, getIds, MEMO_CATEGORIES } from '../reducers';

export const SORT_DATE_FORMAT = 'yyyyMMdd';

const MEMO_NAMES = {
  low: 'Postcard',
  mid: 'Note',
  high: 'Flag',
  severe: 'Alert',
  note: 'Message',
  video: 'Video',
  gallery: 'Photo',
};
const MEMO_ICONS = {
  low: 'ca-priority-low',
  mid: 'ca-priority-mid',
  high: 'ca-priority-high',
  severe: 'ca-priority-severe',
  note: 'ca-sticky-note',
  video: 'ca-videocam',
  gallery: 'ca-photo',
};

export const getActivityForSelectedOrganisation = createSelector(
  reducers.getSelectedId(reducers.ORGANISATIONS),
  reducers.statistics.getOrganisationsCurrentActivity,
  (orgId: string, activity) => activity[orgId]
);

export const checkInCount = createSelector(getIds(CHECK_INS), (ids) => (<Array<string>>ids).length);

export const announcementScoreTally = createSelector(
  reducers.getAnnouncementState,
  (state: any) => state.totalCount
);

export const checkInScoreTally = createSelector(getAll(CHECK_INS), (checkIns: CheckInDto[]) => {
  return tallyCheckIns(checkIns);
});

export const groupCheckInsByDate = (
  checkIns: Array<CheckInDto> = []
): { [date: string]: CheckInDto[] } => {
  return (<Array<any>>checkIns).reduce((accum, checkIn) => {
    const datePipe = new DatePipe();
    const date = datePipe.transform(new Date(checkIn.created), 'custom', SORT_DATE_FORMAT);
    if (accum[date]) {
      accum[date].push(checkIn);
    } else {
      accum[date] = [checkIn];
    }
    return accum;
  }, {});
};

export const groupedCheckInsByDate = createSelector(getAll(CHECK_INS), (checkIns: any[]) =>
  groupCheckInsByDate(checkIns)
);

export const tallyCheckInsByDate = (checkIns: { [date: string]: CheckInDto[] }) => {
  return Object.getOwnPropertyNames(checkIns).reduce((final, date) => {
    final[date] = checkIns[date].reduce(
      (accum, checkIn) => {
        switch (checkIn.score) {
          case 0:
            accum.poor++;
            return accum;
          case 1:
            accum.fair++;
            return accum;
          case 2:
            accum.good++;
            return accum;
          case 3:
            accum.excellent++;
            return accum;
          default:
            return accum;
        }
      },
      { excellent: 0, good: 0, fair: 0, poor: 0 }
    );
    return final;
  }, {});
};

export const checkInTallyByDate = createSelector(groupedCheckInsByDate, (checkIns) =>
  tallyCheckInsByDate(checkIns)
);

export const checkInTalliesForDateRange = createSelector(
  checkInTallyByDate,
  reducers.statistics.getFilters,
  (checkIns, filters) => {
    const all = {};
    const datePipe = new DatePipe();
    for (var d = filters.startDate; isBefore(d, filters.endDate); d = addDays(d, 1)) {
      all[datePipe.transform(d, 'custom', SORT_DATE_FORMAT)] =
        checkIns[datePipe.transform(d, 'custom', SORT_DATE_FORMAT)];
    }
    return all;
  }
);

export const popularCategories = (
  stats: { [categoryId: string]: number },
  categories: { [id: string]: CategoryDto }
) => {
  return Object.getOwnPropertyNames(stats)
    .map((value) => ({
      score: stats[value],
      ...categories[value],
    }))
    .filter((cat) => cat.score > 0)
    .filter((cat) => cat.name !== undefined)
    .sort((a: any, b: any) => {
      if (a.score > b.score) {
        return -1;
      }
      if (a.score === b.score) {
        return 0;
      }
      return 1;
    });
};

export const getPopularCategories = createSelector(
  getActivityForSelectedOrganisation,
  getEntities(MEMO_CATEGORIES),
  (stats, categories: { [id: string]: CategoryDto }) => {
    if (!stats?.memoCreatedByCategory) {
      return [];
    }
    return popularCategories(stats.memoCreatedByCategory, categories);
  }
);

export const getActiveMembers = createSelector(
  reducers.statistics.getMembersStats,
  getEntities(reducers.MEMBERS),
  (stats, staff = {}) => {
    if (!stats || !stats.data) {
      return [];
    }
    return stats.data
      .map((stat) => ({ ...stat, member: staff[stat.memberId] as MemberDto }))
      .filter((stat) => !!stat)
      .sort((a, b) => {
        if (a.memoCreated === undefined) {
          a.memoCreated = 0;
        }
        if (b.memoCreated === undefined) {
          b.memoCreated = 0;
        }
        if (a.memoCreated < b.memoCreated) {
          return 1;
        }
        if (a.memoCreated > b.memoCreated) {
          return -1;
        }
        return 0;
      });
  }
);

export const getActiveCustomers = createSelector(
  reducers.statistics.getCustomersStats,
  getEntities('customers'),
  (stats, customers) => {
    if (!stats || !stats.data) {
      return [];
    }
    return stats.data
      .map((stat) => ({ ...stat, customer: customers[stat.customerId] }))
      .sort((a, b) => {
        if (a.memoCreated === undefined) {
          a.memoCreated = 0;
        }
        if (b.memoCreated === undefined) {
          b.memoCreated = 0;
        }
        if (a.memoCreated < b.memoCreated) {
          return 1;
        }
        if (a.memoCreated > b.memoCreated) {
          return -1;
        }
        return 0;
      });
  }
);

export const checkInCountForSelectedCustomer = createSelector(
  checkInsForSelectedCustomer,
  (checkIns) => (checkIns as Array<any>).length
);

export const popularMemoTypes = (data: { [type: string]: number }) => {
  return data
    ? Object.getOwnPropertyNames(data)
        .map((type) => ({
          name: MEMO_NAMES[type],
          icon: MEMO_ICONS[type],
          score: data[type],
        }))
        .filter((type) => type.score > 0)
        .sort((a, b) => {
          if (a.score < b.score) {
            return 1;
          }
          if (a.score > b.score) {
            return -1;
          }
          return 0;
        })
    : [];
};

export const tallyCheckIns = (checkIns: Array<CheckInDto>) => {
  return checkIns.reduce(
    (accum, checkIn) => {
      switch (checkIn.score) {
        case 0:
          accum.poor++;
          return accum;
        case 1:
          accum.fair++;
          return accum;
        case 2:
          accum.good++;
          return accum;
        case 3:
          accum.excellent++;
          return accum;
        default:
          return accum;
      }
    },
    { excellent: 0, good: 0, fair: 0, poor: 0 }
  );
};

export const tallyGroupedCheckIns = (checkIns: { [id: string]: Array<CheckInDto> }) => {
  return Object.getOwnPropertyNames(checkIns).reduce((final, date) => {
    final[date] = tallyCheckIns(checkIns[date]);
    return final;
  }, {});
};

export const customerCheckInTalliesForDateRange = createSelector(
  checkInsForSelectedCustomer,
  reducers.statistics.getFilters,
  (checkIns, filters) => {
    const groups = tallyGroupedCheckIns(groupCheckInsByDate(checkIns));
    const all = {};
    for (var d = filters.startDate; isBefore(d, filters.endDate); d = addDays(d, 1)) {
      const datePipe = new DatePipe();
      const formattedDate = datePipe.transform(d, 'custom', SORT_DATE_FORMAT);
      all[formattedDate] = groups[formattedDate];
    }
    return all;
  }
);

export const customerCheckInTotals = createSelector(checkInsForSelectedCustomer, tallyCheckIns);

export const getCustomerStats = createSelector(
  reducers.getStatisticsState,
  (state) => state.memosByCustomer
);

export const getSelectedCustomerStatistics = createSelector(
  reducers.getSelectedId(CUSTOMERS),
  getCustomerStats,
  (customerId, statistics) => statistics.data[customerId as string]
);

export const getSelectedCustomerMemoCount = createSelector(
  getSelectedCustomerStatistics,
  (stats: any) => stats && stats.total
);

export const getPopularCategoriesForCustomer = createSelector(
  getSelectedCustomerStatistics,
  getEntities(MEMO_CATEGORIES),
  (customerStats, categories: { [id: string]: CategoryDto }) => {
    if (customerStats) {
      return popularCategories(customerStats as any, categories);
    }
    return [];
  }
);
